import DOMPurify from 'dompurify';

// Liste blanche partagée avec Utils/DescriptionSanitizer.cs (server) et
// SiteCapteurs/Utils/DescriptionSanitizer.cs (point d'écriture d'Event.Description) :
// du texte et du formatage de texte, rien d'exécutable. data-list ne sert qu'à
// distinguer les listes à puces des listes numérotées quand le DOM Quill est réutilisé tel quel.
const ALLOWED_TAGS = ['p', 'br', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'u', 's', 'del'];
const ALLOWED_ATTR = ['data-list'];

const ALLOWED_LIST_VALUES = new Set(['bullet', 'ordered']);
const HTTP_HREF_PATTERN = /^https?:\/\//i;

// data-list n'a de sens que sur un <li>, avec l'une des deux valeurs Quill. Un lien n'est
// conservé que s'il pointe vers http(s) (VIT-04) : essayé avec ALLOWED_URI_REGEXP, mais
// dompurify 3.4.15 supprime alors `target` même listé dans ALLOWED_ATTR (vérifié : sans
// cette option `target="_blank"` survit) — un hook explicite l'évite. dompurify n'ajoute
// pas `rel="noopener"` de lui-même sur les target="_blank" qu'il conserve (aucune mention
// de "_blank"/"noopener" dans sa source) : on le pose nous-mêmes, LinkedinPostController.cs
// n'ajoutant que `target` sans `rel`.
function restrictAttributes(node: Element): void {
    if (node.hasAttribute('data-list')) {
        const value = node.getAttribute('data-list');
        if (node.tagName !== 'LI' || !value || !ALLOWED_LIST_VALUES.has(value)) {
            node.removeAttribute('data-list');
        } // if
    } // if
    if (node.tagName === 'A') {
        const href = node.getAttribute('href');
        if (!href || !HTTP_HREF_PATTERN.test(href)) {
            node.removeAttribute('href');
        } else if (node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
        } // if
    } // if
} // function restrictAttributes(node: Element): void

DOMPurify.addHook('afterSanitizeAttributes', restrictAttributes);

export function sanitizeHtml(html: string | null | undefined): string {
    if (!html) {
        return '';
    } // if
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
} // export function sanitizeHtml(html: string | null | undefined): string

// Variante LinkedIn : mêmes règles + `a`, dont le schéma est restreint à http/https (VIT-04)
// -- les posts contiennent des liens externes légitimes (ex. lnkd.in), donc pas de
// restriction de domaine, seulement de schéma (voir restrictAttributes). `target`/`rel`
// sont autorisés car LinkedinPostController.cs réécrit chaque lien en
// `<a target="_blank" href="...">` avant de le servir : sans ça, ALLOWED_ATTR (liste
// blanche) supprime `target` et les liens s'ouvrent dans le même onglet.
export function sanitizeLinkedinHtml(html: string | null | undefined): string {
    if (!html) {
        return '';
    } // if
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [...ALLOWED_TAGS, 'a'],
        ALLOWED_ATTR: [...ALLOWED_ATTR, 'href', 'target', 'rel'],
    });
} // export function sanitizeLinkedinHtml(html: string | null | undefined): string
